import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneCall, PhoneOff,
  ChevronLeft, ChevronRight, RefreshCw, Check, Hand, Undo2,
  MessageSquarePlus, X, Trash2, Pencil, Volume2, BarChart3, MapPin, AlertTriangle, Eye,
} from 'lucide-react'
import { apiGet, postJson } from '../lib/api.js'
import { useApptTypes, ApptTypeCombo, ApptTypeFilter } from '../components/ApptType.jsx'
import CallRecordingPlayer from '../components/phone/CallRecordingPlayer.jsx'
import CallDashboard from './CallDashboard.jsx'
import { useMe } from '../lib/me.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { nameInitial } from '../lib/text.js'
import {
  Page, PageHeader, Tabs, SubTabs, FilterCard, Field, Input, SearchInput, Select,
  Switch, ChoiceChips, DataTable, useTableState, Drawer, Modal, ConfirmDialog, useToast,
  Loading, Badge, Button, IconButton, PatientName, patientHref,
  fmtTime, fmtDateTime, fmtDateText, toISODate, bkkTodayISO,
} from '../kit'

/**
 * Call page (`/calls`) — monitor & manage the phone log from Xano (api:FeVDPm_m).
 *
 * Two tabs:
 *   • สายที่ไม่ได้รับ (missed queue)  — the actionable worklist: ALL un-done missed
 *     calls (any date). Each number is a card that can be claimed (atomic lock),
 *     annotated, and marked done. 3 states: NEW / กำลังดูแล / เสร็จแล้ว.
 *   • สายทั้งหมด (daily monitor)      — read-only view of one day's calls, all
 *     directions/results, filterable.
 *
 * Both group calls by the external party number and refresh live via the shared
 * `call_update` Xano Realtime channel (see lib/realtime.js). We skip events
 * echoing our own employee_id (our optimistic update already refetched).
 */

const asList = (r) => (Array.isArray(r) ? r : Array.isArray(r?.items) ? r.items : [])

const PER_PAGE = 500      // matches the Xano endpoint default
const MAX_PAGES = 20      // hard safety cap: never pull more than PER_PAGE*MAX_PAGES rows

// Paginated fetch — walks Xano's paging envelope until the last page (or the
// safety cap). This is the "responsible caller" guarantee: no single logical
// load can ever pull the whole phone table unbounded.
async function fetchAllPages(params) {
  const out = []
  let page = 1
  for (let i = 0; i < MAX_PAGES; i++) {
    const r = await apiGet('/api/phone', { ...params, page, per_page: PER_PAGE })
    const items = asList(r)
    out.push(...items)
    const cur = Number(r?.curPage ?? page)
    const total = Number(r?.pageTotal ?? 1)
    if (!items.length || cur >= total) break
    page = cur + 1
  }
  return out
}

// External party number: inbound = who called us; outbound = who we dialled.
function partyNumber(r) {
  const n = r?.direction === 'outbound'
    ? (r.caller_destination || r.last_destination)
    : (r.caller_id_number || r.source_number)
  return (n || r?.caller_id_number || r?.caller_destination || '').toString().trim() || 'ไม่ทราบเบอร์'
}
// Match key for a number across formats — last 9 digits (a Thai mobile reads the
// same whether stored 08x… or +668x…). Used to line up an outbound callback with
// the inbound missed call it answers.
const normNum = (n) => { const d = String(n || '').replace(/\D/g, ''); return d.length > 9 ? d.slice(-9) : d }

// Objective "this missed call was actually worked" signal, straight from the phone
// log — independent of the manual เสร็จสิ้น click. Evidence = we DIALLED the number
// back (outbound, answered or not — even a no-answer is an attempt) OR they called
// back and we ANSWERED (inbound answered). Returns number(norm) → latest evidence ts.
//
// 🚨 THIS IS A SERVER AGGREGATE NOW, AND IT MUST STAY ONE. It used to be
//
//     Promise.all([ fetchAllPages({direction:'outbound', 30-day window}),
//                   fetchAllPages({direction:'inbound', call_result:'answered', …}) ])
//
// — ~26,476 full call records with their patient/employee addons (measured
// 2026-08-26), two concurrent page-walks at per_page=500, thrown away to build this
// Map. It re-fired on every `call_update` in every open tab, and that burst served
// Cloudflare Error 1102 to the whole clinic on 24, 25 and 26 Aug: caught live on the
// production Worker 2026-08-26 07:26–07:33Z, 25 of 595 requests 503 `exceededCpu`,
// 23 of them this triple, always in the same second. Pages Functions are ONE Worker,
// so it killed page loads that had nothing to do with the Call page.
//
// It was ALSO silently wrong: fetchAllPages stops at MAX_PAGES (10,000 of 26,476
// rows) by breaking out of the loop, so the evidence it never fetched was
// indistinguishable from "no callback" and the green badge under-reported.
//
// Xano api 644 now joins the worklist to the evidence in SQL and answers with one
// row per number on screen — 10 rows, ~1ms. The number key and the timestamp rule
// are duplicated in that script; normNum()/stamp() below are still the definition,
// so ANY change to either has to be made in both places or the two stop lining up.
async function fetchEvidence(view, day) {
  const params = view === 'done' ? { view, day } : { view }
  const r = await apiGet('/api/phone/callback-evidence', params)
  const map = new Map()
  for (const row of (Array.isArray(r?.items) ? r.items : [])) {
    const k = String(row?.num || '')
    const ts = Number(row?.ts)
    if (!k || !Number.isFinite(ts)) continue
    map.set(k, ts)
  }
  return map
}
// `start_stamp` is the Bangkok wall-clock time stored as a UTC epoch — i.e. the
// number reads +7h ahead of the true instant (json.start_stamp confirms it). So
// we subtract the fixed +7 offset to recover the real instant, after which the
// Asia/Bangkok formatters below render the correct local time. `created_at` is
// already a true UTC epoch, so it's used as-is. (Thailand is a fixed UTC+7, no
// DST, so the constant offset is safe.)
const BKK_OFFSET_MS = 7 * 60 * 60 * 1000
const stamp = (r) => {
  if (r?.start_stamp) return Number(r.start_stamp) - BKK_OFFSET_MS
  if (r?.created_at) return Number(r.created_at)
  return 0
}

// Full call timeline for ONE number (all directions & results), for the popup —
// the worklist itself only holds this number's *missed* rows, so on its own the
// drawer can't show the outbound callback or the answered follow-up. Matched
// patients are pulled cheaply by patient_id; unknown numbers fall back to a bounded
// date-window scan filtered client-side. Window = from the earliest of this number's
// known calls (capped at 60 days back) through today, so a later callback is caught.
async function fetchNumberHistory(g) {
  const today = todayBkk()
  const dates = g.calls.map((c) => c.date).filter(Boolean).sort()
  const floor = shiftDay(today, -60)
  let start = dates[0] || shiftDay(today, -14)
  if (start < floor) start = floor
  if (g.patient_id) {
    return fetchAllPages({ patient_id: g.patient_id, start_date: start, end_date: today })
  }
  const key = normNum(g.number)
  const rows = await fetchAllPages({ start_date: start, end_date: today })
  return rows.filter((r) => normNum(partyNumber(r)) === key)
}

// ---- formatting — dates/times come from the kit (src/kit/format.js, Bangkok) ----
const dayKey = (ts) => (ts ? toISODate(Number(ts)) : '')
function fmtDur(sec) {
  const s = Number(sec) || 0
  if (!s) return '—'
  const m = Math.floor(s / 60)
  return `${m}:${String(s % 60).padStart(2, '0')}`
}
function fmtPhone(n) {
  const d = String(n || '').replace(/\D/g, '')
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
  if (d.length === 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`
  return n
}

// ---- day helpers (YYYY-MM-DD, Bangkok) ----
const todayBkk = () => bkkTodayISO()
function shiftDay(day, delta) {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + delta)
  return dt.toISOString().slice(0, 10)
}

// One friendly timestamp (Bangkok): "วันนี้ 09:23" / "เมื่อวาน 14:20" / "12/07/2026 14:20".
function fmtWhen(ts) {
  if (!ts) return '—'
  const day = dayKey(ts)
  const today = todayBkk()
  if (day === today) return `วันนี้ ${fmtTime(Number(ts))}`
  if (day === shiftDay(today, -1)) return `เมื่อวาน ${fmtTime(Number(ts))}`
  return fmtDateTime(Number(ts))
}

// For a missed inbound call the duration is ring/wait time, not talk time.
const waitLabel = (r) => (r?.call_result === 'missed' ? 'รอสาย' : 'คุย')

// Recordings exist only for answered calls (streamed by uuid via /api/call-recording;
// the payload's voice.* fields are often empty even when a recording exists, so
// `answered` is the reliable "can ฟังสาย" signal). Group-level = any answered call.
const canListen = (c) => c?.call_result === 'answered'
const groupCanListen = (g) => g.calls.some(canListen)

const trunc = (s, n = 40) => { const t = String(s || ''); return t.length > n ? `${t.slice(0, n)}…` : t }

// Phone number with a leading phone icon; click to copy the RAW digits (no dashes).
// stopPropagation so copying inside a table row doesn't also open the drawer.
function CopyPhone({ number, className = '' }) {
  const [copied, setCopied] = useState(false)
  const digits = String(number || '').replace(/\D/g, '')
  const copy = async (e) => {
    e.stopPropagation()
    if (!digits) return
    try { await navigator.clipboard.writeText(digits); setCopied(true); setTimeout(() => setCopied(false), 1200) } catch { /* ignore */ }
  }
  return (
    <button type="button" onClick={copy} title={digits ? 'คัดลอกเบอร์ (ไม่มีขีด)' : undefined}
      className={`group inline-flex items-center gap-1 tabular-nums hover:text-brand-600 ${className}`}>
      {copied ? <Check size={11} className="flex-none text-emerald-500" /> : <Phone size={11} className="flex-none text-slate-400 group-hover:text-brand-500" />}
      {fmtPhone(number)}
    </button>
  )
}

// Client-side search over a call group: phone number, patient name, or HN.
function groupMatches(g, query) {
  const s = query.trim().toLowerCase()
  if (!s) return true
  const digits = s.replace(/\D/g, '')
  if (digits && String(g.number || '').replace(/\D/g, '').includes(digits)) return true
  const p = g.patient
  if (p) {
    if (p.fullname && p.fullname.toLowerCase().includes(s)) return true
    if (p.HN) {
      const hn = String(p.HN).toLowerCase()
      if (hn.includes(s)) return true
      if (digits && hn.replace(/\D/g, '').includes(digits)) return true
    }
  }
  return false
}

// Group calls by external number → one card per person/number. `evidence` is the
// number→callbackTs map from fetchEvidence (optional; absent on the monitor tab).
function groupByNumber(rows, evidence) {
  const map = new Map()
  for (const r of rows) {
    const key = partyNumber(r)
    let g = map.get(key)
    if (!g) { g = { number: key, calls: [], patient: null, patient_id: null }; map.set(key, g) }
    g.calls.push(r)
    if (!g.patient && r.patient) { g.patient = r.patient; g.patient_id = r.patient_id }
  }
  const groups = [...map.values()]
  for (const g of groups) {
    g.calls.sort((a, b) => stamp(b) - stamp(a))
    g.rep = g.calls[0]                                   // latest call = representative task row
    g.latest = stamp(g.rep)
    g.missed = g.calls.filter((c) => c.call_result === 'missed')
    g.openMissed = g.missed.filter((c) => !c.done)       // un-done missed → the work
    // Status is a property of the NUMBER, not just its latest call.
    //  • activeLock = someone is holding it RIGHT NOW (lock === true)  → กำลังดูแล
    //  • touched    = it was picked up before (lock_timestamp set) even if since
    //                 released → it's no longer "ใหม่"                → ค้างต่อ
    //  • done       = nothing left open                               → เสร็จแล้ว
    //  • else                                                          → ใหม่
    g.activeLock = g.calls.find((c) => !c.done && c.lock === true) || null
    g.touched = g.calls.find((c) => !c.done && c.lock_timestamp) || null
    g.lockedCall = g.activeLock || g.touched   // whoever last handled it (for display)
    if (g.missed.length > 0 && g.openMissed.length === 0) g.status = 'done'
    else if (g.activeLock) g.status = 'locked'
    else if (g.touched) g.status = 'touched'
    else g.status = 'new'
    // Callback evidence: a dial-back / answered-back to this number AFTER the most
    // recent missed call. (Before the miss doesn't count — that's a prior contact.)
    const lastMissedTs = g.missed.length ? Math.max(...g.missed.map(stamp)) : 0
    const cbTs = evidence ? (evidence.get(normNum(g.number)) || 0) : 0
    g.callbackTs = cbTs > lastMissedTs ? cbTs : 0
    g.hasCallback = g.callbackTs > 0
  }
  groups.sort((a, b) => b.latest - a.latest)
  return groups
}

// Result / status pills are kit <Badge>s — tone carries the meaning (kit/Badge.jsx).
const RESULT_META = {
  missed: { label: 'ไม่ได้รับ', tone: 'rose', Icon: PhoneMissed },
  answered: { label: 'รับสาย', tone: 'emerald', Icon: PhoneCall },
  cancelled: { label: 'ยกเลิก', tone: 'slate', Icon: X },
  failed: { label: 'ล้มเหลว', tone: 'amber', Icon: PhoneOff },
}
function ResultBadge({ result }) {
  const rm = RESULT_META[result] || RESULT_META.cancelled
  return <Badge tone={rm.tone}><rm.Icon size={11} />{rm.label}</Badge>
}

// direction label/icon — includes internal (local) calls
const dirLabel = (d) => (d === 'outbound' ? 'สายออก' : d === 'local' ? 'ภายใน' : 'สายเข้า')
const dirIcon = (d) => (d === 'outbound' ? PhoneOutgoing : d === 'local' ? Phone : PhoneIncoming)
// inbound = green (สายเข้า), outbound = sky, internal = slate
const dirColor = (d) => (d === 'outbound' ? 'text-sky-500' : d === 'local' ? 'text-slate-300' : 'text-emerald-500')
// Quick reasons offered when someone closes a call with NO callback trace + NO note
// — a soft nudge so a "bare close" is at least explained and attributed, not blocked.
// The prefix constant is shared with the dashboard's derived KPI text.
const NO_CALLBACK_PREFIX = 'ปิดโดยไม่มีสายโทรกลับ'
const CLOSE_REASONS = ['โทรกลับจากมือถือ', 'ติดต่อทาง LINE', 'เบอร์ผิด / ไม่มีผู้รับสาย', 'เป็นสายซ้ำ', 'อื่นๆ']

const STATUS_META = {
  new: { label: 'ใหม่', tone: 'rose' },
  locked: { label: 'กำลังดูแล', tone: 'amber' },
  touched: { label: 'ค้างต่อ', tone: 'brand' },
  done: { label: 'เสร็จแล้ว', tone: 'emerald' },
}

export default function CallList() {
  const me = useMe()
  const [tab, setTab] = useState('missed')           // 'missed' | 'monitor'
  const [rows, setRows] = useState([])
  const [evidence, setEvidence] = useState(new Map()) // number(norm) → latest callback ts (missed tab only)
  const [loading, setLoading] = useState(true)       // blocking (first load / context change)
  const [refreshing, setRefreshing] = useState(false) // background (realtime / manual) — keeps list
  const [error, setError] = useState(null)
  // Row-action results (ลบแท็ก) are toasts. Separate from `error`: that one is the
  // LOAD error — it replaces the list and every refetch clears it.
  const toast = useToast()
  const [tick, setTick] = useState(0)                // bump → refetch
  const [liveHint, setLiveHint] = useState(false)    // small "มีอัปเดตใหม่" pulse
  const [mineOnly, setMineOnly] = useState(false)    // missed tab (open view): only calls I'm handling
  const [missedView, setMissedView] = useState('open') // missed tab: 'open' (worklist) | 'done' (resolved, by day)
  const [selectedNumber, setSelectedNumber] = useState(null) // open drawer's group (external party number)
  const [lastOpened, setLastOpened] = useState(() => { try { return localStorage.getItem('call:lastOpened') || null } catch { return null } }) // sticky "you looked here" marker, survives closing the drawer + reloads
  const [summaryReload, setSummaryReload] = useState(0)      // header refresh → CallDashboard reload signal
  // Search is per-tab-context so typing in one tab doesn't leak into the other.
  const [monitorSearch, setMonitorSearch] = useState('')       // สายทั้งหมด (รายวัน)
  const [missedDoneSearch, setMissedDoneSearch] = useState('') // สายที่ไม่ได้รับ · เสร็จแล้ว

  // monitor filters
  const [day, setDay] = useState(todayBkk())
  const [dir, setDir] = useState('')                 // '' | inbound | outbound
  const [result, setResult] = useState('')           // '' | missed | answered | cancelled
  const [apptType, setApptType] = useState('')       // appointmentList id ('' = ทุกประเภท)

  const refetch = useCallback(() => setTick((t) => t + 1), [])

  // Open a row's drawer AND remember it as the last-opened one (sticky highlight in
  // the table so you keep your place after closing). Persisted so it survives reload.
  const openRow = useCallback((num) => {
    setSelectedNumber(num)
    if (num) { setLastOpened(num); try { localStorage.setItem('call:lastOpened', num) } catch { /* ignore */ } }
  }, [])

  // Clear the appointment-type tag on a whole number, straight from the table row.
  const clearTag = useCallback(async (g) => {
    try {
      await Promise.all(g.calls.map((c) => postJson('/api/phone', { phone_id: c.id, request_type: 'set_appointmentlist', appointmentlist_id: '' })))
      toast.success('ลบแท็กแล้ว')
    } catch (e) {
      // say so — otherwise the × looks like it did nothing. Refetch still runs so a
      // partial success (some calls cleared) shows what actually changed.
      toast.error('ลบแท็กประเภทนัดไม่สำเร็จ', { detail: e?.message || String(e) })
    }
    refetch()
  }, [refetch, toast])

  // Which cards currently have an open note editor (by phone number). While any
  // are open we HOLD realtime refreshes so the data can't shift under someone
  // mid-type; the held refresh is applied the moment the last editor closes.
  const editingKeys = useRef(new Set())
  const pendingRefresh = useRef(false)
  const setEditing = useCallback((key, open) => {
    const s = editingKeys.current
    if (open) s.add(key); else s.delete(key)
    if (s.size === 0 && pendingRefresh.current) { pendingRefresh.current = false; refetch() }
  }, [refetch])

  // A context change (tab/day/filter) blocks with the spinner — old data is
  // irrelevant. A background refresh (manual/realtime) keeps the list mounted so
  // in-progress work (a half-typed note, an expanded card) survives.
  const lastCtx = useRef(null)
  useEffect(() => {
    if (tab === 'summary') return    // the dashboard tab fetches its own data
    let alive = true
    const ctxKey = `${tab}|${missedView}|${day}|${dir}|${result}`
    const contextChanged = lastCtx.current !== ctxKey
    lastCtx.current = ctxKey
    if (contextChanged) setLoading(true); else setRefreshing(true)
    setError(null)
    // Missed OPEN = every un-done missed call, ANY date (the `done=false` +
    // `missed` filters keep this an inherently small worklist). Missed DONE =
    // resolved missed calls for ONE chosen day (bounded, could otherwise be huge).
    // Monitor = one chosen day, any direction/result. All page-walked + capped.
    const base = tab === 'monitor'
      ? { start_date: day, end_date: day, direction: dir || undefined, call_result: result || undefined }
      : missedView === 'open'
        ? { call_result: 'missed', done: 'false' }
        : { call_result: 'missed', done: 'true', start_date: day, end_date: day }
    // appointmentlist_id tag filter — server-side, applies to every list view
    const params = { ...base, appointmentlist_id: apptType || undefined }
    fetchAllPages(params)
      .then((rows) => { if (alive) { setRows(rows); setLoading(false); setRefreshing(false); setLiveHint(false) } })
      .catch((e) => { if (alive) { setError(e.message || 'โหลดไม่สำเร็จ'); setLoading(false); setRefreshing(false) } })
    return () => { alive = false }
  }, [tab, missedView, day, dir, result, apptType, tick])

  // Callback evidence (missed tab only) — whether each missed number was actually
  // dialled back. The WINDOW NOW LIVES IN api 644, not here: open worklist = last 30
  // days (still-open misses are recent by nature), done view = the chosen day ±3.
  // Moving it server-side is deliberate — the two have to agree with the worklist the
  // same query derives, and a window computed in the browser is how they would drift.
  // Kept out of the main list effect (not apptType/dir/result-filtered — a callback
  // proves contact regardless of tag). Re-runs on `tick` so an agent's own just-logged
  // call-back flips the badge green before they click เสร็จสิ้น; the proxy's 15s edge
  // cache is sized to stay inside that gap.
  useEffect(() => {
    if (tab !== 'missed') { setEvidence(new Map()); return }
    let alive = true
    fetchEvidence(missedView, day)
      .then((m) => { if (alive) setEvidence(m) })
      .catch(() => { /* badge just stays neutral on failure */ })
    return () => { alive = false }
  }, [tab, missedView, day, tick])

  // Realtime: refetch on any call update. Bursts (resolving a multi-call number
  // fires several PATCHes → several broadcasts) are coalesced via a short
  // debounce, and held entirely while a note is being typed. `employee_id`
  // self-echo skip is a no-op today (backend omits it) but kept for later.
  const liveTimer = useRef(null)
  useXanoRealtime('call_update', (msg) => {
    const p = msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    if (emp != null && me?.employee_id != null && String(emp) === String(me.employee_id)) return
    setLiveHint(true)
    if (editingKeys.current.size > 0) { pendingRefresh.current = true; return }  // hold while typing
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(() => refetch(), 400)
  })
  useEffect(() => () => clearTimeout(liveTimer.current), [])

  const groups = useMemo(() => groupByNumber(rows, evidence), [rows, evidence])
  // Derive the drawer's group from the live groups (not a snapshot) so a realtime
  // refetch keeps its content fresh — and if the number drops out of the set (e.g.
  // marked done in the missed-open worklist) the drawer closes on its own.
  const selectedGroup = useMemo(
    () => (selectedNumber != null ? groups.find((g) => g.number === selectedNumber) || null : null),
    [groups, selectedNumber],
  )
  // close the drawer when the tab / day / view changes out from under it
  useEffect(() => { setSelectedNumber(null) }, [tab, missedView, day])

  // "เฉพาะที่ฉันดูแล": narrow the missed queue to numbers I currently hold the lock on.
  const meId = me?.employee_id
  const searchable = tab === 'monitor' || (tab === 'missed' && missedView === 'done')
  const activeSearch = tab === 'monitor' ? monitorSearch : missedDoneSearch  // the search value for the current tab
  const visibleGroups = useMemo(() => {
    let gs = groups
    if (tab === 'missed' && missedView === 'open' && mineOnly) {
      gs = gs.filter((g) => meId != null && String(g.activeLock?.lock_by) === String(meId))
    }
    if (searchable && activeSearch.trim()) gs = gs.filter((g) => groupMatches(g, activeSearch))
    return gs
  }, [groups, mineOnly, tab, missedView, meId, searchable, activeSearch])
  const nMine = useMemo(
    () => groups.filter((g) => meId != null && String(g.activeLock?.lock_by) === String(meId)).length,
    [groups, meId],
  )

  // Filter "differs from default" per view → ล้างตัวกรอง enabled only then.
  const today = todayBkk()
  const canClear = tab === 'monitor'
    ? day !== today || !!dir || !!result || !!apptType || !!monitorSearch
    : missedView === 'open'
      ? mineOnly || !!apptType
      : day !== today || !!apptType || !!missedDoneSearch
  const clearFilters = () => {
    setApptType('')
    if (tab === 'monitor') { setDay(today); setDir(''); setResult(''); setMonitorSearch('') }
    else if (missedView === 'open') setMineOnly(false)
    else { setDay(today); setMissedDoneSearch('') }
  }
  // "No match" (filters hide everything) vs "empty" (nothing there): only the
  // client-side narrowing (search / เฉพาะที่ฉันดูแล) can hide loaded rows.
  const narrowed = (searchable && !!activeSearch.trim()) || (tab === 'missed' && missedView === 'open' && mineOnly)
  const clearNarrowing = () => { setMineOnly(false); if (tab === 'monitor') setMonitorSearch(''); else setMissedDoneSearch('') }
  const emptyTitle = tab === 'monitor'
    ? 'ไม่มีสายในวันนี้'
    : missedView === 'done' ? 'ไม่มีสายที่จัดการแล้วในวันนี้' : 'ไม่มีสายที่ไม่ได้รับค้างอยู่'

  return (
    <Page width="wide">
      <PageHeader
        title={<span className="inline-flex items-center gap-2.5">สายโทรศัพท์<LiveDot on={liveHint} /></span>}
        subtitle="บันทึกการโทรทุกสาขา · จัดการสายที่ไม่ได้รับ · ดูภาพรวมคุณภาพสาย"
        actions={
          /* one refresh in the page header for every tab. On ภาพรวม it drives the
             dashboard reload (bumps summaryReload → CallDashboard refetches), so the
             dashboard no longer needs its own top refresh button. */
          <Button variant="secondary" icon={RefreshCw}
            loading={tab !== 'summary' && (loading || refreshing)}
            onClick={tab === 'summary' ? () => setSummaryReload((n) => n + 1) : refetch}>
            รีเฟรช
          </Button>
        }
      >
        <Tabs value={tab} onChange={setTab} items={[
          { key: 'missed', label: 'สายที่ไม่ได้รับ', icon: PhoneMissed },
          { key: 'monitor', label: 'สายทั้งหมด (รายวัน)', icon: Phone },
          { key: 'summary', label: 'ภาพรวม', icon: BarChart3 },
        ]} />
      </PageHeader>

      {tab === 'summary' ? (
        <CallDashboard reloadKey={summaryReload} />
      ) : tab === 'monitor' ? (
        <FilterCard onClear={clearFilters} canClear={canClear}>
          <Field label="วันที่"><DayNav day={day} setDay={setDay} /></Field>
          <Field label="ทิศทาง" className="w-[150px]">
            <Select value={dir} onChange={setDir} clearable placeholder="ทุกทิศทาง"
              options={[{ value: 'inbound', label: 'สายเข้า' }, { value: 'outbound', label: 'สายออก' }, { value: 'local', label: 'ภายใน' }]} />
          </Field>
          <Field label="ผล" className="w-[150px]">
            <Select value={result} onChange={setResult} clearable placeholder="ทุกสถานะ"
              options={[{ value: 'answered', label: 'รับสาย' }, { value: 'missed', label: 'ไม่ได้รับ' }, { value: 'cancelled', label: 'ยกเลิก' }, { value: 'failed', label: 'ล้มเหลว' }]} />
          </Field>
          <Field label="ประเภทนัด"><ApptTypeFilter value={apptType} onChange={setApptType} /></Field>
          <Field label="ค้นหา" className="w-[240px]">
            <SearchInput value={monitorSearch} onChange={setMonitorSearch} placeholder="เบอร์ / ชื่อ / HN…" />
          </Field>
        </FilterCard>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-3 flex-wrap">
            <SubTabs value={missedView} onChange={setMissedView}
              items={[{ key: 'open', label: 'ค้างอยู่' }, { key: 'done', label: 'เสร็จแล้ว' }]} />
            <p className="text-[12.5px] text-slate-400">
              {missedView === 'open' ? 'ยังไม่ได้จัดการ (ทุกวัน)' : 'สายที่ไม่ได้รับซึ่งจัดการแล้ว'}
            </p>
          </div>
          <FilterCard onClear={clearFilters} canClear={canClear}>
            {missedView === 'open' ? (
              <>
                <Field label="ประเภทนัด"><ApptTypeFilter value={apptType} onChange={setApptType} /></Field>
                <div className="pb-2">
                  <Switch size="sm" checked={mineOnly} onChange={setMineOnly}
                    label={`เฉพาะที่ฉันดูแล${nMine ? ` (${nMine})` : ''}`} />
                </div>
              </>
            ) : (
              <>
                <Field label="วันที่"><DayNav day={day} setDay={setDay} /></Field>
                <Field label="ประเภทนัด"><ApptTypeFilter value={apptType} onChange={setApptType} /></Field>
                <Field label="ค้นหา" className="w-[240px]">
                  <SearchInput value={missedDoneSearch} onChange={setMissedDoneSearch} placeholder="เบอร์ / ชื่อ / HN…" />
                </Field>
              </>
            )}
          </FilterCard>
        </>
      )}

      {/* Body (list tabs only — the summary tab renders its own dashboard above).
          A context change (tab/day/filter) shows the spinner: the old rows belong to
          another view. A background refresh keeps the rows on screen, dimmed. */}
      {tab !== 'summary' && (
        <>
          {tab === 'missed' && missedView === 'open' && !loading && !error && groups.length > 0 && <QueueSummary groups={groups} />}
          <CallTable key={`${tab}|${missedView}|${day}`}
            groups={loading ? [] : visibleGroups} tab={tab} me={me} selected={selectedNumber} lastOpened={lastOpened}
            onSelect={openRow} onClearTag={clearTag}
            loading={loading || refreshing} error={error} onRetry={refetch}
            filtered={narrowed} onClearFilters={clearNarrowing} emptyTitle={emptyTitle}
          />
        </>
      )}

      {/* side drawer — one number's full detail + actions, for both list tabs */}
      {selectedGroup && (
        <CallCard key={selectedGroup.number} g={selectedGroup} me={me} tab={tab} onDone={refetch} onEditing={setEditing}
          onClose={() => setSelectedNumber(null)} />
      )}
    </Page>
  )
}

/* ------------------------------------------------------------------ card --- */

// One number's full detail + actions, as the kit's right-hand Drawer. Used for
// both list tabs (it used to be a card that could also sit inline — the inline
// mode was never rendered, so only the drawer form is kept).
//
// No `record` on the Drawer on purpose: it shows a NUMBER (a group of call rows),
// not one stored row, so a single "#id · สร้าง … · แก้ไข …" line would mislead.
const OK_MSG = {
  note: 'บันทึกโน้ตแล้ว', note_edit: 'แก้ไขโน้ตแล้ว', note_delete: 'ลบโน้ตแล้ว',
  claim: 'รับเรื่องแล้ว', release: 'ปล่อยเรื่องแล้ว', done: 'ปิดเคสแล้ว',
}

function CallCard({ g, me, tab, onDone, onEditing, onClose }) {
  const toast = useToast()
  const [busy, setBusy] = useState(null)            // 'claim' | 'release' | 'done' | 'note' | 'note_delete' | 'note_edit' | 'tag'
  const [noteOpen, setNoteOpen] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [editId, setEditId] = useState(null)        // key of the note being edited inline
  const [editText, setEditText] = useState('')
  const [confirm, setConfirm] = useState(null)      // { kind:'done' | 'done_reason' } | { kind:'note_delete', note }
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [closeReason, setCloseReason] = useState('') // chosen quick-reason for a bare close
  const [closeOther, setCloseOther] = useState('')   // free text when reason = อื่นๆ
  const isMissed = tab === 'missed'

  // Full call timeline for this number. null = still loading. The worklist row set
  // only holds this number's missed calls, so we fetch the rest (outbound callbacks,
  // answered follow-ups) on open. Keyed on number/patient so a realtime refetch of
  // the group doesn't re-trigger the history fetch.
  const [fullCalls, setFullCalls] = useState(null)
  useEffect(() => {
    let alive = true
    setFullCalls(null)
    fetchNumberHistory(g)
      .then((r) => { if (alive) setFullCalls(r) })
      .catch(() => { if (alive) setFullCalls([]) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.number, g.patient_id])
  // Merge fetched history with the worklist's own rows (dedupe by id), newest first.
  const timeline = useMemo(() => {
    const map = new Map()
    for (const c of [...(fullCalls || []), ...g.calls]) if (c && c.id != null) map.set(c.id, c)
    return [...map.values()].sort((a, b) => stamp(b) - stamp(a))
  }, [fullCalls, g.calls])

  // Tell the parent when this card is mid-edit (adding OR editing a note) so
  // realtime refreshes are held (and released on close/unmount) — protects
  // in-progress text from being wiped.
  useEffect(() => {
    const editing = noteOpen || editId != null
    onEditing?.(g.number, editing)
    return () => onEditing?.(g.number, false)
  }, [noteOpen, editId, g.number, onEditing])

  const rep = g.rep
  const activeLock = g.activeLock         // call actively locked right now (lock === true)
  const meId = me?.employee_id
  const lockedByMe = meId != null && activeLock != null && String(activeLock.lock_by) === String(meId)
  const lockedByOther = g.status === 'locked' && !lockedByMe
  const touchedBy = g.status === 'touched' ? g.touched : null   // released-but-not-done → who last handled it
  const notes = Array.isArray(rep.note) ? rep.note.filter((n) => n?.detail) : []
  const patientRef = g.patient ? { ...g.patient, id: g.patient_id } : null

  // Returns true on success. `quiet` skips the success toast (a step of a bigger action).
  async function act(request_type, extra, { quiet = false } = {}) {
    setBusy(request_type)
    let ok = false
    try {
      await postJson('/api/phone', { phone_id: rep.id, request_type, ...extra })
      ok = true
      if (!quiet && OK_MSG[request_type]) toast.success(OK_MSG[request_type])
    } catch (e) {
      toast.error('ทำรายการไม่สำเร็จ', { detail: e.message || undefined })
    } finally {
      setBusy(null)
      onDone()          // refetch → re-sync
    }
    return ok
  }

  // Group-wide action: apply a request_type to every call in a set (in parallel).
  // Claim/release/done all operate on the whole NUMBER — รับเรื่อง locks every
  // open call, ปล่อย releases every locked call, เสร็จสิ้น closes every open call.
  async function bulk(request_type, calls) {
    if (!calls.length) { onDone(); return }
    setBusy(request_type)
    try {
      await Promise.all(calls.map((c) => postJson('/api/phone', { phone_id: c.id, request_type })))
      if (OK_MSG[request_type]) toast.success(OK_MSG[request_type])
    } catch (e) {
      toast.error('ทำรายการไม่สำเร็จ', { detail: e.message || undefined })
    } finally {
      setBusy(null)
      onDone()
    }
  }
  const lockedCalls = g.calls.filter((c) => !c.done && (c.lock || c.lock_timestamp))
  const claimAll = () => bulk('claim', g.openMissed)
  const releaseAll = () => bulk('release', lockedCalls)
  const doneAll = () => bulk('done', g.openMissed)

  // Tag every call in this number with an appointment type (or clear it). Applied
  // to the whole group, consistent with claim/done — the tag describes the number.
  async function setTag(appointmentlist_id) {
    setBusy('tag')
    try {
      await Promise.all(g.calls.map((c) => postJson('/api/phone', { phone_id: c.id, request_type: 'set_appointmentlist', appointmentlist_id })))
      toast.success(appointmentlist_id ? 'บันทึกแท็กแล้ว' : 'ลบแท็กแล้ว')
    } catch (e) {
      toast.error('ทำรายการไม่สำเร็จ', { detail: e.message || undefined })
    } finally {
      setBusy(null)
      onDone()
    }
  }
  const currentTag = g.calls.find((c) => c.appointmentlist_id)?.appointmentlist_id ?? rep.appointmentlist_id ?? ''

  async function submitNote() {
    const detail = noteText.trim()
    if (!detail) { setNoteOpen(false); return }
    await act('note', { detail })
    setNoteText(''); setNoteOpen(false)
  }
  // discard the in-progress note without saving (button or Esc)
  const cancelNote = () => { setNoteText(''); setNoteOpen(false) }

  // Stable per-note key (unique_id → created_date → position) for inline edit.
  const nkey = (n, i) => n.unique_id || (n.created_date != null ? `cd${n.created_date}` : `i${i}`)
  const startEdit = (key, detail) => { setEditId(key); setEditText(detail) }
  const cancelEdit = () => { setEditId(null); setEditText('') }
  async function saveEdit(n) {
    const detail = editText.trim()
    if (!detail) return
    await act('note_edit', { unique_id: n.unique_id, created_date: n.created_date, detail })
    cancelEdit()
  }
  // Esc inside a note box cancels THAT box only — stopPropagation keeps it from
  // also reaching the Drawer's Esc (which would ask "ทิ้งการแก้ไข?").
  const escOnly = (e, fn) => { if (e.key === 'Escape') { e.stopPropagation(); fn() } }

  // Does this number show any sign of being worked? A logged callback OR a note.
  // When neither is true, closing is a "bare close" → route through the reason prompt.
  const hasNote = notes.length > 0
  const worked = g.hasCallback || hasNote
  const clearClose = () => { setConfirm(null); setCloseReason(''); setCloseOther('') }
  const reasonReady = !!closeReason && !(closeReason === 'อื่นๆ' && !closeOther.trim())

  // Terminal/destructive actions go through a ConfirmDialog (or the reason Modal)
  // first; the dialog stays open + busy while the request runs.
  async function runConfirm() {
    const c = confirm
    if (!c || confirmBusy) return
    setConfirmBusy(true)
    try {
      if (c.kind === 'done_reason') {
        const reason = closeReason === 'อื่นๆ' ? (closeOther.trim() || 'อื่นๆ') : closeReason
        await act('note', { detail: `${NO_CALLBACK_PREFIX}: ${reason}` }, { quiet: true })
        await doneAll()
      } else if (c.kind === 'done') await doneAll()
      else if (c.kind === 'note_delete') await act('note_delete', { unique_id: c.note.unique_id, created_date: c.note.created_date })
    } finally {
      setConfirmBusy(false)
      clearClose()
    }
  }

  // Unsaved text in the note box (or an inline edit open) → the Drawer asks first.
  const dirty = (noteOpen && !!noteText.trim()) || editId != null

  return (
    <Drawer open onClose={onClose} title="รายละเอียดสาย" busy={!!busy || confirmBusy} dirty={dirty}
      discardMessage="โน้ตที่พิมพ์ไว้ยังไม่ได้บันทึก — จะหายไป">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {/* header */}
        <div className="flex items-center gap-3 p-3.5">
          {g.patient_id ? (
            <a href={patientHref(g.patient_id)} target="_blank" rel="noopener noreferrer" draggable={false}
              className="flex-none rounded-full" title="เปิดหน้าคนไข้ในแท็บใหม่">
              <Avatar patient={g.patient} number={g.number} result={rep.call_result} clickable />
            </a>
          ) : (
            <Avatar patient={g.patient} number={g.number} result={rep.call_result} />
          )}
          <div className="min-w-0 flex-1">
            {/* identity */}
            <div className="flex items-center gap-2 flex-wrap">
              {g.patient ? (
                <span className="text-[15px] min-w-0">
                  <PatientName patient={patientRef} inline />
                </span>
              ) : (
                <CopyPhone number={g.number} className="text-[15px] font-semibold text-slate-900" />
              )}
              {isMissed && <StatusBadge status={g.status} />}
              {isMissed && <CallbackBadge g={g} />}
              {g.patient?.branch?.name && <span className="text-[12px] text-slate-400">{g.patient.branch.name}</span>}
            </div>
            {/* one meta line — bits joined by middots */}
            <div className="mt-1 flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[12.5px] leading-tight">
              {(() => {
                const bits = []
                if (g.patient) bits.push(<CopyPhone key="ph" number={g.number} className="text-slate-500" />)
                if (g.calls.length > 1) {
                  bits.push(<span key="cnt" className="text-slate-500">{`${g.calls.length} สาย${isMissed && g.openMissed.length ? ` · ค้าง ${g.openMissed.length}` : ''}`}</span>)
                } else if (isMissed) {
                  bits.push(<span key="dur" className="text-slate-400">{waitLabel(rep)} {fmtDur(rep.call_duration)}</span>)
                }
                if (isMissed) bits.push(<span key="when" className="text-slate-600 tabular-nums">{fmtWhen(g.latest)}</span>)
                if (lockedByOther) bits.push(<span key="lo" className="text-amber-600">{activeLock?.employee_lock_by?.code || activeLock?.lock_by}</span>)
                if (lockedByMe) bits.push(<span key="lm" className="text-amber-600 font-medium">คุณกำลังดูแล</span>)
                if (touchedBy) bits.push(<span key="tb" className="text-sky-600">เคยรับโดย {touchedBy.employee_lock_by?.code || touchedBy.lock_by}</span>)
                if (g.status === 'done' && rep.employee_done_by?.code) bits.push(<span key="db" className="text-emerald-600">เสร็จโดย {rep.employee_done_by.code}</span>)
                return bits.flatMap((n, i) => (i === 0 ? [n] : [<Sep key={`s${i}`} />, n]))
              })()}
            </div>
          </div>
        </div>

        {/* actions — โน้ต on ANY call (no claim required, both tabs); the claim/done
            flow stays specific to the missed worklist. */}
        <div className="flex items-center gap-1.5 flex-wrap px-3.5 pb-3 -mt-1">
          <Button size="sm" variant="secondary" icon={MessageSquarePlus} onClick={() => setNoteOpen((v) => !v)}>โน้ต</Button>
          {isMissed && (
            <MissedActions
              status={g.status} lockedByMe={lockedByMe} lockedByOther={lockedByOther} busy={busy}
              onClaim={claimAll} onTakeover={claimAll}
              onRelease={releaseAll}
              onDone={() => setConfirm({ kind: worked ? 'done' : 'done_reason' })}
            />
          )}
        </div>

        {/* appointment-type tag — editable + deletable */}
        <div className="px-3.5 pb-3">
          <Field label={
            <>
              ประเภทนัด (แท็ก)
              {currentTag ? (
                <button type="button" onClick={() => setTag('')} disabled={busy === 'tag'}
                  className="ml-2 text-[11.5px] text-rose-500 hover:text-rose-600 disabled:opacity-50">ลบแท็ก</button>
              ) : null}
            </>
          }>
            <div className={busy === 'tag' ? 'opacity-50 pointer-events-none' : ''}>
              <ApptTypeCombo value={currentTag} onChange={setTag} placeholder="ยังไม่ได้แท็ก…" />
            </div>
          </Field>
        </div>

        {/* per-call rows: the FULL timeline for the number — every call across
            directions, date-grouped — not just the missed rows the worklist loaded.
            Notes come AFTER this, so the record detail reads first. */}
        <div className="border-t border-slate-100 bg-slate-50/40">
          <div className="flex items-center gap-1.5 px-3.5 pt-2.5 pb-0.5">
            <span className="text-[11.5px] text-slate-400">ประวัติการโทรทั้งหมด{timeline.length ? ` · ${timeline.length} สาย` : ''}</span>
            {fullCalls == null && <Loading variant="inline" label="" />}
          </div>
          <div className="divide-y divide-slate-50">
            {timeline.map((c, i, arr) => {
              const showDate = i === 0 || dayKey(stamp(c)) !== dayKey(stamp(arr[i - 1]))
              return (
                <div key={c.id}>
                  {showDate && <div className="px-3.5 pt-2 pb-0.5 text-[11px] font-medium text-slate-500">{fmtDateText(stamp(c), { weekday: true })}</div>}
                  <CallRow c={c} detailed />
                </div>
              )
            })}
          </div>
        </div>

        {noteOpen && (
          <div className="px-3.5 py-3 border-t border-slate-100 flex items-center gap-2">
            <Input autoFocus value={noteText} onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); escOnly(e, cancelNote) }}
              placeholder="บันทึกโน้ต เช่น โทรกลับแล้ว นัดวันจันทร์…" className="flex-1" />
            <Button size="sm" variant="secondary" onClick={cancelNote} disabled={busy === 'note'}>ยกเลิก</Button>
            <Button size="sm" onClick={submitNote} loading={busy === 'note'}>บันทึก</Button>
          </div>
        )}

        {/* notes — the last section; edit/delete enabled only for the note's author */}
        {notes.length > 0 && (
          <div className="px-3.5 pb-2.5 space-y-1.5 border-t border-slate-100 pt-2">
            <div className="text-[11.5px] text-slate-400 mb-0.5">โน้ต</div>
            {notes.slice(-3).map((n, i) => {
              const key = nkey(n, i)
              const mine = meId != null && String(n.created_by) === String(meId)
              const editingThis = editId === key
              return (
                <div key={key} className="group flex items-start gap-1.5 text-[12.5px] text-slate-600">
                  <MessageSquarePlus size={12} className="mt-1.5 flex-none text-slate-300" />
                  {editingThis ? (
                    <div className="flex-1 flex items-center gap-1">
                      <Input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(n); escOnly(e, cancelEdit) }}
                        className="flex-1" />
                      <IconButton size="sm" icon={Check} label="บันทึก" onClick={() => saveEdit(n)} disabled={busy === 'note_edit' || !editText.trim()} />
                      <IconButton size="sm" icon={X} label="ยกเลิก" onClick={cancelEdit} />
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 min-w-0 pt-1">{n.detail}</span>
                      <IconButton size="sm" icon={Pencil} label="แก้ไขโน้ต" onClick={() => startEdit(key, n.detail)}
                        disabled={!mine} disabledReason="แก้ไขได้เฉพาะโน้ตที่คุณสร้าง" />
                      <IconButton size="sm" icon={Trash2} label="ลบโน้ต" variant="danger-soft"
                        onClick={() => setConfirm({ kind: 'note_delete', note: n })}
                        disabled={!mine || busy === 'note_delete'} disabledReason={!mine ? 'ลบได้เฉพาะโน้ตที่คุณสร้าง' : undefined} />
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* confirms render INSIDE the drawer so Esc closes them, not the drawer */}
      {confirm?.kind === 'done' && (
        <ConfirmDialog title="ปิดเคสนี้ (เสร็จสิ้น)?"
          message={`สายที่ค้างของเบอร์ ${fmtPhone(g.number)} (${g.openMissed.length} สาย) จะถูกปิดและออกจากรายการค้าง`}
          confirmLabel="ยืนยันเสร็จสิ้น" busy={confirmBusy} onConfirm={runConfirm} onCancel={clearClose} />
      )}
      {confirm?.kind === 'note_delete' && (
        <ConfirmDialog tone="danger" title="ลบโน้ต?"
          message={`โน้ต "${trunc(confirm.note?.detail)}" จะถูกลบถาวร`}
          confirmLabel="ลบ" busy={confirmBusy} onConfirm={runConfirm} onCancel={clearClose} />
      )}
      {/* Bare close: no callback in the phone log and no note. Don't block — ask why,
          and record the reason as a note so the close is explained + attributable. */}
      <Modal open={confirm?.kind === 'done_reason'} onClose={clearClose} busy={confirmBusy} width="sm"
        icon={AlertTriangle} iconTone="amber" title="ปิดเคสโดยไม่มีสายโทรกลับ?"
        subtitle="ยังไม่พบสายโทรกลับถึงเบอร์นี้ในระบบ และไม่มีโน้ต — ปิดเคสเพราะอะไร?"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={clearClose} disabled={confirmBusy}>ยกเลิก</Button>
            <Button onClick={runConfirm} loading={confirmBusy} disabled={!reasonReady}
              disabledReason={!closeReason ? 'เลือกเหตุผลก่อน' : 'ระบุเหตุผลก่อน'}>
              บันทึกเหตุผล + ปิดเคส
            </Button>
          </div>
        }>
        <ChoiceChips value={closeReason} onChange={setCloseReason} options={CLOSE_REASONS.map((r) => ({ value: r, label: r }))} />
        {closeReason === 'อื่นๆ' && (
          <Input autoFocus value={closeOther} onChange={(e) => setCloseOther(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && closeOther.trim()) runConfirm() }}
            placeholder="ระบุเหตุผล…" className="mt-3" />
        )}
      </Modal>
    </Drawer>
  )
}

/* ----------------------------------------------------------------- table --- */
// One row per external number. Columns adapt by tab: the missed worklist leads
// with สถานะ/ผู้ดูแล (who owns it), the daily monitor with ผล/ทิศทาง (what happened).
// Clicking a row opens the drawer; the full detail + actions live there, so the
// table stays scannable. The patient's name / HN opens the patient (new tab) and
// does NOT open the drawer (kit PatientName stops the click).
function CallTable({ groups, tab, me, selected, lastOpened, onSelect, onClearTag, loading, error, onRetry, filtered, onClearFilters, emptyTitle }) {
  const isMissed = tab === 'missed'
  const meId = me?.employee_id
  const { idToLabel } = useApptTypes()   // resolve the appointmentlist_id tag → label
  const tagOf = (g) => {
    const id = g.calls.find((c) => c.appointmentlist_id)?.appointmentlist_id
    return id != null ? (idToLabel.get(String(id)) || null) : null
  }
  /* ประเภทนัด is a MISSED-QUEUE artefact: the tag is set while working a callback,
     so on the daily monitor the column was 100% "—" and ate 17% of the width for
     nothing. Hidden there, and its share handed back to คนไข้/เบอร์ + ล่าสุด — the two
     columns that actually truncate. The filter above the table still works; it just
     no longer has a dead column under it. */
  const columns = [
    {
      key: 'who', label: 'คนไข้ / เบอร์', width: isMissed ? '25%' : '33%',
      render: (g) => {
        const rep = g.rep
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar patient={g.patient} number={g.number} result={rep.call_result} />
            <div className="min-w-0">
              <div className="truncate">
                {g.patient
                  ? <PatientName patient={{ ...g.patient, id: g.patient_id }} inline />
                  : <CopyPhone number={g.number} className="text-slate-800 font-medium" />}
              </div>
              <div className="text-[12px] text-slate-400 whitespace-nowrap">
                {g.patient ? <CopyPhone number={g.number} /> : dirLabel(rep.direction)}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      key: 'rec', width: '5%', align: 'center',
      label: <span title="มีไฟล์เสียง"><Volume2 size={13} className="inline" /></span>,
      render: (g) => (groupCanListen(g)
        ? <span title="มีไฟล์เสียงให้ฟัง (เปิดเพื่อฟัง)"><Volume2 size={15} className="inline text-brand-500" /></span>
        : <span className="text-slate-200">—</span>),
    },
    isMissed
      ? {
          key: 'status', label: 'สถานะ', width: '12%',
          render: (g) => (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <StatusBadge status={g.status} />
              <CallbackBadge g={g} compact />
            </span>
          ),
        }
      : {
          key: 'dir', label: 'ทิศทาง', width: '14%',
          render: (g) => {
            const DirIcon = dirIcon(g.rep.direction)
            return <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] text-slate-500"><DirIcon size={13} className={dirColor(g.rep.direction)} />{dirLabel(g.rep.direction)}</span>
          },
        },
    isMissed
      ? {
          key: 'carer', label: 'ผู้ดูแล', width: '12%',
          render: (g) => {
            const carer = g.activeLock?.employee_lock_by?.code || g.touched?.employee_lock_by?.code
              || (g.status === 'done' ? g.rep.employee_done_by?.code : '') || ''
            const mineHere = meId != null && String(g.activeLock?.lock_by) === String(meId)
            return carer
              ? <span className={`whitespace-nowrap text-[12.5px] ${mineHere ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>{mineHere ? 'คุณ' : carer}</span>
              : <span className="text-slate-300">—</span>
          },
        }
      : { key: 'result', label: 'ผล', width: '14%', render: (g) => <ResultBadge result={g.rep.call_result} /> },
    ...(isMissed ? [{
      // appointment-type tag — deletable (× removes it without opening the drawer)
      key: 'tag', label: 'ประเภทนัด', width: '17%',
      render: (g) => {
        const t = tagOf(g)
        return t
          ? <Badge tone="violet" className="max-w-full">
              <span className="truncate max-w-[130px]">{t}</span>
              <button type="button" title="ลบแท็ก" aria-label="ลบแท็ก"
                onClick={(e) => { e.stopPropagation(); onClearTag(g) }}
                className="flex-none text-violet-300 hover:text-violet-600"><X size={11} /></button>
            </Badge>
          : <span className="text-slate-300">—</span>
      },
    }] : []),
    { key: 'latest', label: 'ล่าสุด', width: isMissed ? '14%' : '17%', render: (g) => <span className="text-slate-500 tabular-nums whitespace-nowrap">{fmtWhen(g.latest)}</span> },
    { key: 'n', label: 'สาย', width: '6%', align: 'center', render: (g) => <span className="tabular-nums text-slate-500">{g.calls.length}</span> },
    {
      key: 'notes', label: 'โน้ต', width: '6%', align: 'center',
      render: (g) => {
        const n = Array.isArray(g.rep.note) ? g.rep.note.filter((x) => x?.detail).length : 0
        return n ? <span className="tabular-nums text-slate-500">{n}</span> : <span className="text-slate-300">—</span>
      },
    },
    {
      key: 'go', label: '', width: isMissed ? '3%' : '5%', align: 'right',
      render: (g) => (selected !== g.number && lastOpened === g.number
        ? <span title="เปิดล่าสุด"><Eye size={14} className="inline text-violet-400" /></span>
        : <ChevronRight size={14} className="inline text-slate-300" />),
    },
  ]
  // Client paging (the rows arrive whole); the page survives realtime refreshes.
  const t = useTableState(groups, columns, { pageSize: 100 })
  return (
    <DataTable
      columns={columns} {...t.tableProps} fixed minWidth={isMissed ? '1040px' : '880px'}
      rowKey={(g) => g.number}
      onRowClick={(g) => onSelect(g.number)}
      // selected = brand; opened before (drawer now closed) = violet — you keep your place
      rowAccent={(g) => (selected === g.number ? '#60a5fa' : lastOpened === g.number ? '#c4b5fd' : null)}
      rowClassName={(g) => (selected === g.number ? 'bg-brand-50/60' : lastOpened === g.number ? 'bg-violet-50/40' : '')}
      loading={loading} error={error || undefined} onRetry={onRetry}
      filtered={filtered} onClearFilters={onClearFilters}
      empty={{ title: emptyTitle }}
    />
  )
}

// small middot separator
function Sep() { return <span className="text-slate-300">·</span> }

// Note button lives in the drawer header now (available on every call); this set is
// just the missed-worklist ownership flow: claim → release → done.
function MissedActions({ status, lockedByMe, lockedByOther, busy, onClaim, onTakeover, onRelease, onDone }) {
  if (status === 'done') return null
  return (
    <>
      {(status === 'new' || status === 'touched') && (
        <Button size="sm" icon={Hand} loading={busy === 'claim'} onClick={onClaim}>รับเรื่อง</Button>
      )}
      {lockedByOther && (
        <Button size="sm" variant="secondary" icon={Hand} loading={busy === 'claim'} onClick={onTakeover}>รับต่อ</Button>
      )}
      {lockedByMe && <Button size="sm" variant="secondary" icon={Undo2} loading={busy === 'release'} onClick={onRelease}>ปล่อย</Button>}
      <Button size="sm" variant="soft" icon={Check} loading={busy === 'done'} onClick={onDone}>เสร็จสิ้น</Button>
    </>
  )
}

function CallRow({ c, detailed }) {
  const DirIcon = dirIcon(c.direction)
  // routing context, shrunk to icons: caller_id_name = ต้นทาง (📞), last_destination = ปลายทาง (📍).
  const route = []
  if (detailed && c.caller_id_name) route.push(<span key="src" title={`ต้นทาง ${c.caller_id_name}`} className="inline-flex items-center gap-1"><Phone size={11} className="text-slate-400" />{c.caller_id_name}</span>)
  if (detailed && c.last_destination) route.push(<span key="dst" title={`ปลายทาง ${c.last_destination}`} className="inline-flex items-center gap-1"><MapPin size={11} className="text-slate-400" />{c.last_destination}</span>)
  return (
    <div className="px-3.5 py-2 text-[12.5px]">
      <div className="flex items-center gap-2.5 flex-wrap">
        <DirIcon size={13} className={dirColor(c.direction)} />
        {detailed && <span className="text-slate-500">{dirLabel(c.direction)}</span>}
        <ResultBadge result={c.call_result} />
        <span className="text-slate-500 tabular-nums">{fmtWhen(stamp(c))}</span>
        <span className="text-slate-400">· {waitLabel(c)} {fmtDur(c.call_duration)}</span>
        {/* ring time = seconds it rang before answer/hangup — useful on any result */}
        {detailed && c.ring_time ? <span className="text-slate-400">· ริง {c.ring_time}s</span> : null}
        {c.done && c.employee_done_by?.code && <span className="text-emerald-600">· เสร็จโดย {c.employee_done_by.code}</span>}
        {/* recordings only exist for answered calls — hide for missed/cancelled */}
        {c.call_result === 'answered' && <CallRecordingPlayer uuid={c.unique_id} className="ml-auto text-[11.5px]" />}
      </div>
      {route.length > 0 && (
        <div className="mt-0.5 pl-[22px] text-[11.5px] text-slate-400 break-all flex flex-wrap items-center gap-x-3 gap-y-0.5">{route}</div>
      )}
    </div>
  )
}

// The lazy "ฟังเสียง" control lives in components/phone/CallRecordingPlayer.jsx,
// shared with the patient chart's phone log. Read its header before touching
// audio here: it fetches the recording into a Blob on purpose, because
// /api/call-recording now streams the mp3 instead of buffering it inside the
// shared Pages isolate — buffering it there was an Error 1102 path, and this
// control is the one the call centre clicks all day.

/* --------------------------------------------------------------- widgets --- */

function QueueSummary({ groups }) {
  const nNew = groups.filter((g) => g.status === 'new').length
  const nLocked = groups.filter((g) => g.status === 'locked').length
  const nTouched = groups.filter((g) => g.status === 'touched').length
  return (
    <div className="mb-3 flex items-center gap-2 text-[13px] flex-wrap">
      <Badge tone="rose" dot>ใหม่ {nNew}</Badge>
      <Badge tone="amber" dot>กำลังดูแล {nLocked}</Badge>
      {nTouched > 0 && <Badge tone="brand" dot>ค้างต่อ {nTouched}</Badge>}
      <span className="text-slate-400">รวม {groups.length} เบอร์ที่ต้องติดตาม</span>
    </div>
  )
}

function Avatar({ patient, number, result, clickable }) {
  const label = patient ? nameInitial(patient.fullname) : (number || '?').replace(/\D/g, '').slice(-2)
  const missed = result === 'missed'
  return (
    <div className={`h-10 w-10 flex-none rounded-full grid place-items-center text-[14px] font-semibold ${
      patient ? 'bg-brand-100 text-brand-700' : missed ? 'bg-rose-50 text-rose-400' : 'bg-slate-100 text-slate-500'
    } ${clickable ? 'cursor-pointer transition hover:ring-2 hover:ring-brand-200' : ''}`}>
      {patient ? label : (missed ? <PhoneMissed size={16} /> : <Phone size={16} />)}
    </div>
  )
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new
  return <Badge tone={m.tone} dot>{m.label}</Badge>
}

// Objective "was this actually dialled back?" chip (from the phone log, not the
// เสร็จสิ้น click). Green = yes, grey = no trace. Suppressed for brand-new, untouched
// numbers (nobody's worked them yet, so "no callback" is just noise).
function CallbackBadge({ g, compact }) {
  if (g.status === 'new') return null
  const has = g.hasCallback
  const when = has ? fmtWhen(g.callbackTs) : null
  if (compact) {
    return has
      ? <PhoneOutgoing size={13} className="text-emerald-500" title={`โทรกลับแล้ว · ${when}`} />
      : <PhoneOff size={13} className="text-slate-300" title="ยังไม่มีสายโทรกลับในระบบ" />
  }
  return has
    ? <span title={`สายออกถึงเบอร์นี้ · ${when}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"><PhoneOutgoing size={11} /> โทรกลับแล้ว</span>
    : <span title="ไม่พบสายออก/สายรับกลับถึงเบอร์นี้ในระบบโทรศัพท์" className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400"><PhoneOff size={11} /> ไม่มีสายกลับ</span>
}

// One day with ‹ › stepping and วันนี้. The date box is the browser's own date input
// (the kit has no single-day picker yet), shown in kit field styling.
function DayNav({ day, setDay }) {
  return (
    <div className="flex items-center gap-1.5">
      <IconButton icon={ChevronLeft} label="วันก่อนหน้า" variant="secondary" onClick={() => setDay((d) => shiftDay(d, -1))} />
      <div className="w-[160px]"><Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} /></div>
      <IconButton icon={ChevronRight} label="วันถัดไป" variant="secondary" onClick={() => setDay((d) => shiftDay(d, 1))} />
      <Button size="sm" variant="secondary" onClick={() => setDay(todayBkk())}>วันนี้</Button>
    </div>
  )
}

function LiveDot({ on }) {
  return (
    <span className="relative inline-flex h-2 w-2" title="อัปเดตเรียลไทม์">
      {on && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
      <span className={`relative inline-flex rounded-full h-2 w-2 ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    </span>
  )
}
